/**
 * PaymentCancel Component
 *
 * Displayed when user cancels Stripe Checkout.
 * Allows them to try again or contact support.
 *
 * Project: P07 - Stripe Integration
 * Sprint: S03 - Payment Integration
 * Work Unit: WU02 - Payment Flow UI
 */

import { useNavigate } from 'react-router-dom';
import { XCircle, ArrowLeft, HelpCircle } from 'lucide-react';

import { Button, Card, CardBody, CardFooter, Link } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { Brand } from '@enfomentis/shared/config';

// =============================================================================
// Component
// =============================================================================

export function PaymentCancel(): React.JSX.Element {
  const navigate = useNavigate();

  return (
    <AppLayout hideFooter>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
          <div className="space-y-6">
            {/* Cancel Icon */}
            <div className="text-warning flex justify-center">
              <XCircle className="w-20 h-20" strokeWidth={1.5} />
            </div>

            {/* Cancel Message */}
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-text-primary">
                Payment Cancelled
              </h1>
              <p className="text-default-600">
                No worries! Your payment was not processed and you haven&apos;t been charged.
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-default-50 rounded-lg p-4 text-left">
              <p className="text-sm text-default-600">
                You can return to complete your payment at any time. Your plan selection has been saved.
              </p>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                color="primary"
                size="lg"
                className="w-full"
                onPress={() => navigate('/payment')}
                startContent={<ArrowLeft className="w-5 h-5" />}
              >
                Try Again
              </Button>

              <Button
                variant="bordered"
                size="lg"
                className="w-full"
                onPress={() => navigate('/subscribe')}
              >
                Change Plan
              </Button>
            </div>
          </div>
        </CardBody>

        {/* Footer */}
        <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
          <div className="flex items-center gap-2 text-sm text-default-500">
            <HelpCircle className="w-4 h-4" />
            <span>
              Need help?{' '}
              <Link
                href={`mailto:${Brand.supportEmail}`}
                color="primary"
                size="sm"
              >
                Contact support
              </Link>
            </span>
          </div>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}
